import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import Navbar from '../components/Navbar';
import HeroSection from '../components/HeroSection';
import TrustedBy from '../components/TrustedBy';
import HowItWorks from '../components/HowItWorks';
import CallToAction from '../components/CallToAction';
import Footer from '../components/Footer';
import LoginForm from '../components/LoginForm';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';
import SignUpPage from './SignUpPage';

function Home() {
  const [activeModal, setActiveModal] = useState(null);
  const [loginError, setLoginError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLoginSubmit = async (values) => {
    try {
      setLoginError('');
      const role = await login(values.email, values.password);
      setActiveModal(null);
      if (role.toLowerCase() === 'creator') {
        navigate('/dashboard/creator');
      } else if (role.toLowerCase() === 'brand') {
        navigate('/dashboard/brand');
      }
    } catch (error) {
      setLoginError('Username or password is incorrect');
    }
  };

  return (
    <>
      <Helmet>
        <title>CollabOrb - Connect with Top Creators | Creator Marketing Platform</title>
        <meta name="description" content="Discover, connect, and collaborate with top creators on CollabOrb. Empower your brand through impactful social media campaigns." />
        <meta name="keywords" content="Creator marketing platform, brand partnerships, social media creators, collaborate, content creators, digital marketing campaigns, authentic partnerships" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://collaborb.com/" />

        <meta property="og:title" content="CollabOrb - Premier creator Marketing Platform" />
        <meta property="og:description" content="Connect with authentic creators and grow your brand through impactful social media collaborations. Join thousands of successful partnerships." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://collaborb.com/" />
        <meta property="og:image" content="https://collaborb.com/og-home-image.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CollabOrb - Premier creator Marketing Platform" />
        <meta name="twitter:description" content="Connect with authentic creators and grow your brand through impactful social media collaborations." />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "CollabOrb",
            "description": "Premier creator marketing platform connecting brands with authentic content creators",
            "url": "https://collaborb.com",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://collaborb.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            },
            "sameAs": [
              "https://facebook.com/collaborb",
              "https://twitter.com/collaborb",
              "https://instagram.com/collaborb",
              "https://linkedin.com/company/collaborb"
            ]
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "CollabOrb",
            "description": "Leading creator marketing platform",
            "url": "https://collaborb.com",
            "logo": "/collaborb-512.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+1-234-567-8900",
              "contactType": "Customer Service",
              "areaServed": "Worldwide",
              "availableLanguage": "English"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "1250",
              "bestRating": "5",
              "worstRating": "1"
            }
          })}
        </script>
      </Helmet>

      <Navbar
        onJoinClick={() => setActiveModal('signup')}
        onLoginClick={() => setActiveModal('login')}
      />

      <div className="site-shell pt-20">
        <HeroSection onGetStarted={() => setActiveModal('signup')} />
        <TrustedBy />
        <HowItWorks />
        <CallToAction onGetStarted={() => setActiveModal('signup')} />
        <Footer />
      </div>

      <Modal
        isOpen={activeModal === 'signup'}
        onClose={() => setActiveModal(null)}
        overlayClassName="backdrop-blur-md bg-slate-950/45"
        panelClassName="max-w-xl"
        contentClassName="p-3 pt-12 sm:p-4 sm:pt-12"
      >
        <SignUpPage
          isOverlay
          onClose={() => setActiveModal(null)}
          onSwitchToLogin={() => setActiveModal('login')}
        />
      </Modal>

      <Modal isOpen={activeModal === 'login'} onClose={() => {
        setActiveModal(null);
        setLoginError('');
      }}>
        <LoginForm onSubmit={handleLoginSubmit} error={loginError} />
      </Modal>
    </>
  );
}

export default Home;
